import React from 'react'
import { Button } from '@/components/ui/button'
import { 
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { cn } from '@/lib/utils'

interface ContextMenuButtonProps {
  /** Content to render inside the button */
  children: React.ReactNode
  /** Optional className for styling */
  className?: string
  /** Callback when "Open" is clicked */
  onOpen: () => void
  /** Callback when "Open in new tab" is clicked */
  onOpenInNewTab: () => void
  /** Additional props to pass to the Button */
  [key: string]: any
}

/**
 * A reusable button with context menu that provides "Open" and "Open in new tab" options.
 * Used by both ParentNavigationLink and AppLink for consistent UX.
 *
 * Being a button rather than an anchor, it gets none of the browser's new-tab gestures, so it
 * reproduces the two a link has: ctrl/cmd-click and middle-click open in a new tab. Clicks never
 * bubble, since callers sit it inside rows that navigate on click.
 */
export function ContextMenuButton({
  children,
  className,
  onOpen,
  onOpenInNewTab,
  ...props
}: ContextMenuButtonProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (e.ctrlKey || e.metaKey) {
      onOpenInNewTab()
    } else {
      onOpen()
    }
  }

  const handleAuxClick = (e: React.MouseEvent) => {
    if (e.button !== 1) return
    e.preventDefault()
    e.stopPropagation()
    onOpenInNewTab()
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "p-0 h-auto bg-transparent hover:bg-transparent",
            !className && "text-blue-600 hover:text-blue-800 hover:underline underline-offset-4",
            className
          )}
          onClick={handleClick}
          onAuxClick={handleAuxClick}
          {...props}
        >
          {children}
        </Button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          Open
        </ContextMenuItem>
        <ContextMenuItem onClick={(e) => { e.stopPropagation(); onOpenInNewTab(); }}>
          Open in new tab
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}